/**
 * This file contains utilities for using client hints for user preference which
 * are needed by the server, but are only known by the browser.
 */
import { getHintUtils } from '@epic-web/client-hints'
import {
	clientHint as colorSchemeHint,
	subscribeToSchemeChange,
} from '@epic-web/client-hints/color-scheme'
import { clientHint as timeZoneHint } from '@epic-web/client-hints/time-zone'
import * as React from 'react'
import { useRevalidator } from 'react-router'
import { useOptionalRequestInfo, useRequestInfo } from './request-info.ts'

// Helper function to get current quarter
function getCurrentQuarter() {
	const now = new Date();
	const month = now.getMonth() + 1;
	if (month <= 3) return 1;
	if (month <= 6) return 2;
	if (month <= 9) return 3;
	return 4;
}

// Period filter mode type
export type PeriodFilterMode = 'all-time' | 'quarterly';

// Period filter client hints
const periodFilterHints = {
	mode: {
		cookieName: 'period-filter-mode',
		getValueCode: `(function(){ const cookies = document.cookie.split(';').map(c => c.trim()); const cookie = cookies.find(c => c.startsWith('period-filter-mode=')); return cookie ? cookie.split('=')[1] : 'quarterly'; })()`,
		fallback: 'quarterly',
		transform(value: string | null) {
			return (value === 'all-time' ? 'all-time' : 'quarterly') as PeriodFilterMode;
		},
	},
	year: {
		cookieName: 'period-filter-year',
		getValueCode: `(function(){ const cookies = document.cookie.split(';').map(c => c.trim()); const cookie = cookies.find(c => c.startsWith('period-filter-year=')); return cookie ? cookie.split('=')[1] : '${new Date().getFullYear()}'; })()`,
		fallback: new Date().getFullYear().toString(),
		transform(value: string | null) {
			return value || new Date().getFullYear().toString();
		},
	},
	quarter: {
		cookieName: 'period-filter-quarter',
		getValueCode: `(function(){ const cookies = document.cookie.split(';').map(c => c.trim()); const cookie = cookies.find(c => c.startsWith('period-filter-quarter=')); return cookie ? cookie.split('=')[1] : 'Q${getCurrentQuarter()}'; })()`,
		fallback: `Q${getCurrentQuarter()}`,
		transform(value: string | null) {
			return value || `Q${getCurrentQuarter()}`;
		},
	},
}

const hintsUtils = getHintUtils({
	theme: colorSchemeHint,
	timeZone: timeZoneHint,
	...periodFilterHints,
	// add other hints here
})

export const { getHints } = hintsUtils

/**
 * @returns an object with the client hints and their values
 */
export function useHints() {
	const requestInfo = useRequestInfo()
	return requestInfo.hints
}

export function useOptionalHints() {
	const requestInfo = useOptionalRequestInfo()
	return requestInfo?.hints
}

/**
 * Helper hook to get period filter values from hints
 */
export function usePeriodFilters() {
	const hints = useHints()
	return {
		mode: hints.mode as PeriodFilterMode,
		year: hints.year,
		quarter: hints.quarter,
	}
}

/**
 * Helper function to update period filter cookies
 */
export function updatePeriodFilterCookies(
	filters: {
		mode?: PeriodFilterMode
		year?: string
		quarter?: string
	},
	targetUrl?: string
) {
	if (typeof document === 'undefined') return

	let changed = false

	if (filters.mode) {
		document.cookie = `period-filter-mode=${filters.mode}; path=/`
		changed = true
	}
	if (filters.year) {
		document.cookie = `period-filter-year=${filters.year}; path=/`
		changed = true
	}
	if (filters.quarter) {
		document.cookie = `period-filter-quarter=${filters.quarter}; path=/`
		changed = true
	}

	// Navigate to target URL or reload the page to sync the new cookie values with server-side rendering
	if (changed) {
		if (targetUrl) {
			window.location.href = targetUrl
		} else {
			window.location.reload()
		}
	}
}

/**
 * @returns inline script element that checks for client hints and sets cookies
 * if they are not set then reloads the page if any cookie was set to an
 * inaccurate value.
 */
export function ClientHintCheck({ nonce }: { nonce: string }) {
	const { revalidate } = useRevalidator()
	React.useEffect(
		() => {
			if (typeof window !== 'undefined') {
				return subscribeToSchemeChange(() => revalidate())
			}
		},
		[revalidate],
	)

	return (
		<script
			nonce={nonce}
			dangerouslySetInnerHTML={{
				__html: hintsUtils.getClientHintCheckScript(),
			}}
			suppressHydrationWarning
		/>
	)
}
